import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Restore as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { getDeletedEmployees, restoreEmployees, permanentDeleteEmployees } from '../redux/EmployeeCRUD';
import DeletedEmployeesList from './DeletedEmployeesList';

import MessageSnackbar from '@/__core/helpers/components/MessageSnackbar';
import LoadingBackdrop from '@/__core/helpers/components/LoadingBackdrop';
import ConfirmationModal from '@/__core/components/ConfirmationModal';
import { formatErrorForSnackbar, toApiError } from '@/__core/helpers/Error';
import {
  formatEmployeePermanentDeleteError,
  hasEmployeePermanentDeleteRelations,
} from '../utils/employeePermanentDeleteError';

interface Employee {
  id: string;
  code: string;
  name: string;
  email: string;
  jobTitle?: { name: string };
  department?: { name: string };
  company?: { name: string };
  status: unknown;
  deleted_at: string;
  deleted_by_name: string;
}

interface TrashModalEmployeeProps {
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

const TrashModalEmployee: React.FC<TrashModalEmployeeProps> = ({ open, onClose, onRefresh }) => {
  const [deletedEmployees, setDeletedEmployees] = useState<Employee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingActionIds, setPendingActionIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      fetchDeletedEmployees();
    }
  }, [open]);

  const fetchDeletedEmployees = async () => {
    setLoading(true);
    try {
      const response = await getDeletedEmployees();
      
      // Handle different response structures
      let employees = [];
      if (Array.isArray(response.data)) {
        employees = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        employees = response.data.results;
      } else if (response.data && typeof response.data === 'object') {
        // If it's an object but not an array, wrap it in an array
        employees = [response.data];
      }
      
      setDeletedEmployees(employees);
    } catch (error) {
      console.error('Error fetching deleted employees:', error);
      setDeletedEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedEmployees.length === deletedEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(deletedEmployees.map(employee => employee.id));
    }
  };

  const handleSelectEmployee = (employeeId: string) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleRestoreClick = (employeeIds: string[]) => {
    setPendingActionIds(employeeIds);
    setConfirmRestoreOpen(true);
  };

  const handleRestoreConfirm = async () => {
    setConfirmRestoreOpen(false);
    setActionLoading(true);
    setLoadingMessage(`Đang khôi phục ${pendingActionIds.length} nhân viên...`);
    setSuccessMessage(null);
    setErrorMessage(null);
    
    try {
      await restoreEmployees(pendingActionIds);
      setSuccessMessage(`Đã khôi phục ${pendingActionIds.length} nhân viên thành công!`);
      setSelectedEmployees([]);
      fetchDeletedEmployees();
      onRefresh?.();
    } catch (error) {
      setErrorMessage(formatErrorForSnackbar(toApiError(error)) || 'Có lỗi xảy ra khi khôi phục nhân viên!');
    } finally {
      setActionLoading(false);
      setLoadingMessage('');
      setPendingActionIds([]);
    }
  };

  const handlePermanentDeleteClick = (employeeIds: string[]) => {
    setPendingActionIds(employeeIds);
    setConfirmDeleteOpen(true);
  };

  const handlePermanentDeleteConfirm = async () => {
    setConfirmDeleteOpen(false);
    setActionLoading(true);
    setLoadingMessage(`Đang xóa vĩnh viễn ${pendingActionIds.length} nhân viên...`);
    setSuccessMessage(null);
    setErrorMessage(null);
    
    try {
      const response = await permanentDeleteEmployees(pendingActionIds);

      if (hasEmployeePermanentDeleteRelations(response)) {
        setErrorMessage(formatEmployeePermanentDeleteError(response));
        const deletedCount = Number((response as { count?: number })?.count);
        if (Number.isFinite(deletedCount) && deletedCount > 0) {
          setSuccessMessage(`Đã xóa vĩnh viễn ${deletedCount} nhân viên.`);
        }
      } else {
        const deletedCount = Number((response as { count?: number })?.count);
        const successCount =
          Number.isFinite(deletedCount) && deletedCount > 0 ? deletedCount : pendingActionIds.length;
        setSuccessMessage(`Đã xóa vĩnh viễn ${successCount} nhân viên!`);
      }

      setSelectedEmployees([]);
      fetchDeletedEmployees();
      onRefresh?.();
    } catch (error: unknown) {
      const errorData = (error as { response?: { data?: unknown } })?.response?.data;
      setErrorMessage(
        formatEmployeePermanentDeleteError(
          errorData,
          formatErrorForSnackbar(toApiError(error)) || 'Có lỗi xảy ra khi xóa vĩnh viễn nhân viên!',
        ),
      );
    } finally {
      setActionLoading(false);
      setLoadingMessage('');
      setPendingActionIds([]);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e5e7eb',
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Thùng rác ({deletedEmployees.length} nhân viên)
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : deletedEmployees.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
            }}
          >
            <Typography variant="body1" color="textSecondary">
              Không có nhân viên nào trong thùng rác
            </Typography>
          </Box>
        ) : (
          <DeletedEmployeesList
            employees={deletedEmployees}
            selectedEmployees={selectedEmployees}
            onSelectEmployee={handleSelectEmployee}
            onSelectAll={handleSelectAll}
            onRestore={(employeeId: string) => handleRestoreClick([employeeId])}
            onPermanentDelete={(employeeId: string) => handlePermanentDeleteClick([employeeId])}
            isLoading={actionLoading}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: '1px solid #e5e7eb',
          px: 3,
          py: 2,
          gap: 1,
        }}
      >
        {selectedEmployees.length > 0 && (
          <>
            <Button
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={() => handleRestoreClick(selectedEmployees)}
              disabled={actionLoading}
              sx={{
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: '#f0fdf4',
                },
              }}
            >
              Khôi phục ({selectedEmployees.length})
            </Button>
            <Button
              variant="outlined"
              startIcon={<DeleteForeverIcon />}
              onClick={() => handlePermanentDeleteClick(selectedEmployees)}
              disabled={actionLoading}
              sx={{
                borderColor: '#ef4444',
                color: '#ef4444',
                '&:hover': {
                  borderColor: '#dc2626',
                  backgroundColor: '#fef2f2',
                },
              }}
            >
              Xóa vĩnh viễn ({selectedEmployees.length})
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>

    {/* Confirmation Modal for Restore */}
    <ConfirmationModal
      open={confirmRestoreOpen}
      onClose={() => setConfirmRestoreOpen(false)}
      onConfirm={handleRestoreConfirm}
      title="Khôi phục nhân viên"
      message={`Bạn có chắc chắn muốn khôi phục ${pendingActionIds.length} nhân viên đã chọn?`}
      confirmText="Khôi phục"
      cancelText="Hủy"
      isLoading={actionLoading}
    />

    {/* Confirmation Modal for Permanent Delete */}
    <ConfirmationModal
      open={confirmDeleteOpen}
      onClose={() => setConfirmDeleteOpen(false)}
      onConfirm={handlePermanentDeleteConfirm}
      title="Xóa vĩnh viễn nhân viên"
      message={`Bạn có chắc chắn muốn xóa vĩnh viễn ${pendingActionIds.length} nhân viên đã chọn? Hành động này không thể hoàn tác.`}
      confirmText="Xóa vĩnh viễn"
      cancelText="Hủy"
      isLoading={actionLoading}
    />

    {/* Loading Backdrop */}
    <LoadingBackdrop open={actionLoading} message={loadingMessage} />

    {/* Message Snackbar for success */}
    <MessageSnackbar
      open={!!successMessage}
      message={successMessage}
      severity="success"
      onClose={() => setSuccessMessage(null)}
    />

    {/* Message Snackbar for error */}
    <MessageSnackbar
      open={!!errorMessage}
      message={errorMessage}
      severity="error"
      autoHideDuration={errorMessage?.includes('\n') ? 12000 : 6000}
      onClose={() => setErrorMessage(null)}
    />
    </>
  );
};

export default TrashModalEmployee;

